import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Setting2 } from 'iconsax-react';
import { useAuth } from '../../contexts/AuthContext';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-background-card rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto animate-modalOpen">
        <div className="flex items-center justify-between p-6 border-b border-border-dark">
          <h2 className="text-2xl font-semibold text-white">User Settings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {/* User Profile Section */}
            <div className="bg-background-hover/30 rounded-lg p-6 border border-border-dark">
              <h3 className="text-lg font-medium mb-6 text-white flex items-center">
                <Setting2 size={20} className="mr-2 text-blue-400" />
                Profile Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                  <div className="text-white text-base">
                    {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Not provided'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                  <div className="text-white text-base">{user?.username || 'Not provided'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="text-white text-base break-all">{user?.email || 'Not provided'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                  <div className="text-white text-base">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300">
                      {user?.role || 'Not assigned'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${user?.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-white text-base capitalize">{user?.status || 'Unknown'}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Login</label>
                  <div className="text-white text-base">
                    {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never logged in'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Close
              </button>
              <button
                onClick={() => {
                  onClose();
                  // Navigate to user edit with current user's ID
                  navigate(`/tenants?view=users&edit=${user?.id}`);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center"
              >
                <Setting2 size={16} className="mr-2" />
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
